import React, { useState } from 'react';
import styled from 'styled-components';
import { ThemeProvider } from 'styled-components';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { HelmetProvider } from 'react-helmet-async';
import GlobalStyle from './GlobalStyle';
import SEO from './components/SEO';
import Navbar from './components/Navbar';
import Hero from './components/Hero';
import Partners from './components/Partners';
import JoinUs from './components/JoinUs';
import AboutUs from './components/AboutUs';
import Projects from './components/Projects';
import RecentCampaign from './components/RecentCampaign';
import Team from './components/Team';
import Contact from './components/Contact';
import Blog from './components/Blog';
import Footer from './components/Footer';
import NewsPopup from './components/NewsPopup';
import AboutUsPage from './pages/AboutUsPage';
import FormPage from './pages/FormPage';
import EventsPage from './pages/EventsPage';
import BlogPage from './pages/BlogPage';
import ContactPage from './pages/ContactPage';
import CampaignsPage from './pages/CampaignsPage';
import BackgroundDesign from './components/BackgroundDesign';
import CampaignDetailPage from './pages/CampaignDetailPage';
import EventDetailsPage from './pages/EventDetailsPage';
import TeamMemberPage from './pages/TeamMemberPage';
import DonationPage from './pages/DonationPage';
import HbeatJournePage from './pages/HbeatJourneyPage';
import HbeatJourneyDetailPage from './pages/HbeatJourneyDetailPage';
import MediaAppearancesPage from './pages/MediaAppearancesPage';
import Sponsors from './components/Sponsors';
import WhatsAppFloat from './components/WhatsAppFloat';
import FlipBook from './components/FlipBook';
import BlogDetailPage from './pages/BlogDetailPage';
import ProjectVideo from './components/ProjectVideo';
import ProjectVideoPage from './pages/ProjectVideoPage';

const theme = {
  colors: {
    primary: '#990011',
    secondary: '#a48d26',
    dark: '#1A1A1A',
    light: '#FFFFFF',
    background: '#d7d7d7',
    text: '#333333',
    gray: '#6C757D'
  },
  fontSizes: {
    small: '0.875rem',
    medium: '1rem',
    large: '1.25rem',
    xlarge: '1.5rem',
    xxlarge: '2rem'
  }
};

const AppWrapper = styled.div`
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  overflow-x: hidden;
`;

const MainContent = styled.main`
  flex: 1;
  width: 100%;
`;

const SponsorshipSection = styled.section`
  padding: 4rem 0;
  // background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
  position: relative;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    opacity: 0.05;
    z-index: 1;
  }
`;

const SponsorshipContainer = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 2rem;
  position: relative;
  z-index: 2;
`;

const SectionHeader = styled.div`
  text-align: center;
  margin-bottom: 3rem;

  h2 {
    font-size: clamp(2rem, 5vw, 3rem);
    font-weight: 800;
    color: ${props => props.theme.colors.text};
    margin-bottom: 1rem;
    position: relative;

    &::after {
      content: '';
      position: absolute;
      bottom: -10px;
      left: 50%;
      transform: translateX(-50%);
      width: 80px;
      height: 4px;
      background: ${props => props.theme.colors.primary};
      border-radius: 2px;
    }
  }

  p {
    font-size: 1.2rem;
    color: #666;
    max-width: 600px;
    margin: 0 auto;
    line-height: 1.6;
  }
`;

const Layout = ({ children }) => (
  <>
    <Navbar />
    {children}
    <Footer />
    <WhatsAppFloat
      phoneNumber="+212777794712"
      message="Hello! I'd like to get in touch with TBH organization."
    />
  </>
);

const HomePage = () => (
  <MainContent>
    <SEO 
      title="TBH Morocco | The Big Heart Association | Empowering Communities"
      description="The Big Heart Association (TBH Morocco) is a non-profit in Casablanca dedicated to education, healthcare, and humanitarian solidarity. Discover our Heartbeat Journey project transforming 24 schools across Morocco's 12 regions."
      url="/"
      faq={[
        { question: "What is The Big Heart Association?", answer: "The Big Heart Association (TBH) is a Moroccan non-profit organization founded in February 2022 in Casablanca, dedicated to education, healthcare, and humanitarian action in rural communities across Morocco." },
        { question: "What is the Heartbeat Journey project?", answer: "Heartbeat Journey is TBH's flagship 7-year initiative (2024–2031) to renovate 24 schools across Morocco's 12 regions, providing education, medical caravans, and community support." },
        { question: "How can I donate to The Big Heart Association?", answer: "You can donate via bank transfer to 'Association Grand Coeur' at La Banque Populaire, RIB: 190 780 21211 69386710001 68. Visit tbh.ma/donate for details." },
        { question: "Where is The Big Heart Association located?", answer: "TBH is based at Dar Chabab En-Najma, Hay Hassani, Casablanca, Morocco. You can reach us at +212 777794712 or asso.thebigheart@tbh.ma." },
        { question: "How can I volunteer with TBH Morocco?", answer: "Visit tbh.ma/join to fill out our membership form. We welcome volunteers passionate about education, healthcare, and community development in Morocco." }
      ]}
    />
    <h1 style={{ position: 'absolute', width: '1px', height: '1px', padding: 0, margin: '-1px', overflow: 'hidden', clip: 'rect(0,0,0,0)', border: 0 }}>
      The Big Heart Association — Empowering Communities Through Education, Health, and Solidarity in Morocco
    </h1>
    <BackgroundDesign />
    <Hero />
    <Partners />
    <JoinUs />
    <AboutUs />
    <ProjectVideo />
    <RecentCampaign />
    <Contact />
    <Projects />
    <Sponsors />
    {/* Add Sponsorship Section */}
    <SponsorshipSection>
      <SponsorshipContainer>
        <SectionHeader>
          <h2>Sponsorship Opportunities</h2>
          <p>Discover how you can partner with us to create lasting impact in communities across Morocco</p>
        </SectionHeader>
        <FlipBook
          src="https://heyzine.com/flip-book/e089867c67.html"
          title="Partnership & Sponsorship Dossier"
          height="700px"
          showTitle={false}
          showControls={true}
        />
      </SponsorshipContainer>
    </SponsorshipSection>
    <Team />
    <Blog />
  </MainContent>
);

function App() {
  const [showNews, setShowNews] = useState(true);

  return (
    <HelmetProvider>
      <Router>
        <ThemeProvider theme={theme}>
          <GlobalStyle />
        <AppWrapper>
          {showNews && (
            <NewsPopup
              title="Welcome to TBH!"
              message="Welcome to the heart of change."
              duration={7000}
              onClose={() => setShowNews(false)}
            />
          )}
          <Routes>

            <Route path="/" element={
              <Layout>
                <HomePage />
              </Layout>
            } />
            <Route path="/about" element={
              <Layout>
                <AboutUsPage />
              </Layout>
            } />
            <Route path="/project-video" element={
              <Layout>
                <ProjectVideoPage />
              </Layout>
            } />
            <Route path="/overview" element={
              <Navigate to="/project-video" replace />
            } />
            <Route path="/join" element={
              <Layout>
                <FormPage />
              </Layout>
            } />
            <Route path="/events" element={
              <Layout>
                <EventsPage />
              </Layout>
            } />
            <Route path='/events/:slug' element={
              <Layout>
                <EventDetailsPage />
              </Layout>
            } />
            <Route path="/blog" element={
              <Layout>
                <BlogPage />
              </Layout>
            } />
            <Route path="/blog/:blogId" element={
              <Layout>
                <BlogDetailPage />
              </Layout>
            } />
            <Route path="/contact" element={
              <Layout>
                <ContactPage />
              </Layout>
            } />
            <Route path="/actions" element={
              <Layout>
                <CampaignsPage />
              </Layout>
            } />
            <Route path="/actions/:actionId" element={
              <Layout>
                <CampaignDetailPage />
              </Layout>
            } />
            <Route path="/in-the-spotlight" element={
              <Layout>
                <MediaAppearancesPage />
              </Layout>
            } />
            <Route path="/heartbeat-journey" element={
              <Layout>
                <HbeatJournePage />
              </Layout>
            } />
            <Route path="/heartbeat-journey/project/:hbId" element={
              <Layout>
                <HbeatJourneyDetailPage />
              </Layout>
            } />
            <Route path="/donate" element={
              <Layout>
                <DonationPage />
              </Layout>
            } />
            <Route path="/team" element={
              <Layout>
                <TeamMemberPage />
              </Layout>
            } />
            <Route path="/home" element={<Navigate to="/" />} />
            <Route path="*" element={<Navigate to="/" />} />
          </Routes>
        </AppWrapper>
      </ThemeProvider>
    </Router>
    </HelmetProvider>
  );
}

export default App;